import React, { useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { ImageWithBlob } from 'components/ImageWithBlob/ImageWithBlob'

const MIN_SCALE = 1
const MAX_SCALE = 5
// KMP MediaViewerUi.ZoomableImage double-tap target
const DOUBLE_TAP_SCALE = 2.5
const WHEEL_STEP = 1.1

const useStyles = createUseStyles({
  viewport: {
    position: 'relative',
    overflow: 'hidden',
    touchAction: 'none',
    userSelect: 'none',
    cursor: 'zoom-in'
  },
  viewportZoomed: {
    cursor: 'grab'
  },
  viewportDragging: {
    cursor: 'grabbing'
  },
  content: {
    width: '100%',
    height: '100%',
    transformOrigin: 'center center',
    willChange: 'transform'
  }
})

interface ZoomableImageProps {
  src: string
  alt?: string
  className?: string
  imageClassName?: string
  height?: string
  width?: string
  children?: React.ReactNode
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

// How long a click waits for a possible second click before it toggles on its own
const CLICK_DELAY_MS = 220
const DRAG_THRESHOLD_PX = 4

// Pinch/scroll-to-zoom, drag-to-pan and click/double-click-to-toggle image, the web
// counterpart of KMP's ZoomableImage in MediaViewerUi: scale 1x–5x, pan clamped so the photo
// never leaves its frame, a click (or double tap) toggles 2.5x, and everything resets when
// the photo changes.
export const ZoomableImage: React.FC<ZoomableImageProps> = ({
  src,
  alt,
  className,
  imageClassName,
  height,
  width,
  children
}) => {
  const classes = useStyles()
  const viewportRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  // Active pointers (mouse / touch / pen) for drag and two-finger pinch
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null)
  // A press that panned the photo must not count as a click when it ends
  const moved = useRef(false)
  const clickTimer = useRef<number | null>(null)

  useEffect(() => () => window.clearTimeout(clickTimer.current ?? undefined), [])

  useEffect(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
  }, [src])

  const clampOffset = (next: { x: number; y: number }, atScale: number) => {
    const el = viewportRef.current
    if (!el || atScale <= 1) {
      return { x: 0, y: 0 }
    }
    const maxX = ((atScale - 1) * el.clientWidth) / 2
    const maxY = ((atScale - 1) * el.clientHeight) / 2
    return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) }
  }

  // Functional updates: a burst of wheel events in one frame must each build on the last
  const applyScale = (update: (current: number) => number) => {
    setScale((current) => clamp(update(current), MIN_SCALE, MAX_SCALE))
  }

  // Keep the pan inside the frame whenever the scale changes (zooming out re-centres)
  useEffect(() => {
    setOffset((prev) => clampOffset(prev, scale))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale])

  const onWheel = (e: React.WheelEvent) => {
    // Trackpad pinch arrives as ctrl+wheel; a plain wheel zooms too so mouse users can zoom
    e.preventDefault()
    const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP
    applyScale((current) => current * factor)
  }

  const toggleZoom = () => {
    if (scale > 1) {
      setScale(1)
      setOffset({ x: 0, y: 0 })
    } else {
      setScale(DOUBLE_TAP_SCALE)
    }
  }

  // A single click toggles too, but only once it is clear no second click is coming, so a
  // double-click toggles exactly once instead of three times
  const onClick = () => {
    if (moved.current) {
      moved.current = false
      return
    }
    window.clearTimeout(clickTimer.current ?? undefined)
    clickTimer.current = window.setTimeout(toggleZoom, CLICK_DELAY_MS)
  }

  const onDoubleClick = () => {
    window.clearTimeout(clickTimer.current ?? undefined)
    clickTimer.current = null
    toggleZoom()
  }

  const pinchDistance = () => {
    const [a, b] = Array.from(pointers.current.values())
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    moved.current = false
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    if (pointers.current.size === 2) {
      pinchStart.current = { distance: pinchDistance(), scale }
    } else if (scale > 1) {
      setDragging(true)
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const previous = pointers.current.get(e.pointerId)
    if (!previous) {
      return
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2 && pinchStart.current) {
      const target = pinchStart.current.scale * (pinchDistance() / pinchStart.current.distance)
      applyScale(() => target)
      return
    }
    if (pointers.current.size === 1 && scale > 1) {
      const dx = e.clientX - previous.x
      const dy = e.clientY - previous.y
      if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
        moved.current = true
      }
      setOffset((prev) => clampOffset({ x: prev.x + dx, y: prev.y + dy }, scale))
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size < 2) {
      pinchStart.current = null
    }
    if (pointers.current.size === 0) {
      setDragging(false)
    }
  }

  const viewportClass = [
    classes.viewport,
    scale > 1 ? classes.viewportZoomed : '',
    dragging ? classes.viewportDragging : '',
    className || ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={viewportRef}
      className={viewportClass}
      style={{ height, width }}
      onWheel={onWheel}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        className={classes.content}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
      >
        {children ?? (
          <ImageWithBlob
            className={imageClassName}
            height="100%"
            width="100%"
            src={src}
            alt={alt}
            draggable={false}
          />
        )}
      </div>
    </div>
  )
}
